import { describeApiError, type PointsSystem } from '@brush/api-client';
import {
  ErrorNotice,
  ErrorRetry,
  PointsSystemList,
  Spinner,
  currentVersion,
  latestPublished,
} from '@brush/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  useDeletePointsSystem,
  useLeaderboards,
  usePointsSystems,
  useSetPointsSystemArchived,
} from '../../queries';
import { useRoom } from '../../room';
import { useMainScroll } from '../../scroll';
import { useUnsaved } from '../../unsaved';
import { Modal, btnGhost, btnPrimary } from '../Modal';
import { NewPointsSystemDialog, type NewSystemIntent } from './NewPointsSystemDialog';
import { PointsSystemBuilderScreen } from './PointsSystemBuilderScreen';

/**
 * The room's points systems: what it has built, plus everything the platform offers. §9.0's
 * landing screen for the feature.
 *
 * <b>One pane of Settings</b>, so this renders its content and nothing around it — the page
 * heading, the sub-navigation and the margins belong to `SettingsView`, exactly as they do for
 * Templates. A points system is set up once and revisited when something changes, which is what
 * the panes beside it are; it is not a day's work the way the tournaments list or a leaderboard is.
 *
 * <b>And it is not the leaderboard screen.</b> A system is authored here without reference to any
 * leaderboard, and a leaderboard picks up a published one from its own screen. Keeping the two
 * apart is what makes a system reusable across a room's whole season.
 */
export function PointsSystemsPane() {
  const { room, roomId } = useRoom();
  const { leave } = useUnsaved();
  const scrollToTop = useMainScroll();

  const [includeArchived, setIncludeArchived] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [intent, setIntent] = useState<NewSystemIntent | null>(null);
  const [deleting, setDeleting] = useState<PointsSystem | null>(null);

  const systems = usePointsSystems(roomId, includeArchived);
  const leaderboards = useLeaderboards(roomId);
  const archive = useSetPointsSystemArchived(roomId);

  const rows = useMemo(() => systems.data ?? [], [systems.data]);

  // Opening or closing the builder swaps a whole screen, and the two are wildly different
  // heights. Without this the operator lands part-way down a shorter one, looking at blank space.
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, openId]);

  /**
   * How many of *this room's* leaderboards use each system. Counted from the leaderboards the
   * server returned, never invented: there is no usage count on the DTO, and a platform system's
   * total across every room is not a number this room can see or should be shown.
   */
  const usage = useMemo(() => {
    if (!leaderboards.data) return null;
    const counts = new Map<string, number>();
    for (const leaderboard of leaderboards.data) {
      if (leaderboard.pointsSystemId) {
        counts.set(leaderboard.pointsSystemId, (counts.get(leaderboard.pointsSystemId) ?? 0) + 1);
      }
    }
    return new Map(rows.map((s) => [s.id, counts.get(s.id) ?? 0]));
  }, [leaderboards.data, rows]);

  if (!room) return null;

  const open = openId ? rows.find((s) => s.id === openId) ?? null : null;

  // Looked up by id every render rather than held, so the builder sees a new version the moment
  // the catalogue refetches. Leaving routes through the unsaved-work guard, exactly as the
  // section nav and the room switcher do.
  if (open) {
    return (
      <>
        <PointsSystemBuilderScreen
          system={open}
          onBack={() => leave(() => setOpenId(null))}
          onDiscarded={() => setOpenId(null)}
          onCloneRequest={() =>
            setIntent({
              mode: 'clone',
              source: open,
              versionNumber:
                (latestPublished(open) ?? currentVersion(open))?.versionNumber ?? null,
            })
          }
        />
        {intent ? (
          <NewPointsSystemDialog
            intent={intent}
            onClose={() => setIntent(null)}
            onCreated={(created) => {
              setIntent(null);
              setOpenId(created.id);
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <div>
      {/* No heading of its own: the sub-navigation names the pane and the list card below carries
          its own. What belongs here is the sentence that says what a points system is for. */}
      <p className="mb-4 max-w-2xl text-sm text-ink-800/60">
        What a finish is worth. Build one, try it against results you already have, and publish it
        when it looks right — a leaderboard picks one up when it starts and keeps that exact
        version for good.
      </p>

      {systems.isLoading ? (
        <Spinner label="Loading points systems…" />
      ) : systems.isError ? (
        <ErrorRetry
          message="Couldn't load the points systems."
          onRetry={() => void systems.refetch()}
        />
      ) : (
        <PointsSystemList
          systems={rows}
          usage={usage}
          usageLabel="Your leaderboards"
          usageHint="Leaderboards in this room that have pinned a version of this system."
          includeArchived={includeArchived}
          onIncludeArchivedChange={setIncludeArchived}
          onNew={(start) => setIntent({ mode: 'create', start })}
          onOpen={(row) => setOpenId(row.id)}
          onClone={(row) => {
            const system = rows.find((s) => s.id === row.id);
            if (!system) return;
            // A clone copies the version a room adopting it would have been scored by — the
            // newest published one — falling back to the newest of any status for a system that
            // has never published.
            const source = latestPublished(system) ?? currentVersion(system);
            setIntent({ mode: 'clone', source: system, versionNumber: source?.versionNumber ?? null });
          }}
          onArchive={(row) => archive.mutate({ id: row.id, archived: true })}
          onUnarchive={(row) => archive.mutate({ id: row.id, archived: false })}
          onDelete={(row) => setDeleting(rows.find((s) => s.id === row.id) ?? null)}
          // §2.2: a platform system is visible and usable here, and editable by nobody in this
          // room. The list drops its editing controls rather than disabling them, and offers
          // "Clone to edit" in their place.
          canManage={(row) => !row.isPlatform}
          nameOf={(id) => rows.find((s) => s.id === id)?.name ?? null}
          notice={
            leaderboards.isError ? (
              <p className="text-xs text-ink-800/55">
                Couldn’t read this room’s leaderboards, so the usage column is blank rather than
                wrong.
              </p>
            ) : null
          }
        />
      )}

      <ErrorNotice message={describeApiError(archive.error)} className="mt-4" />

      {intent ? (
        <NewPointsSystemDialog
          key={intent.mode === 'create' ? `new-${intent.start}` : `clone-${intent.source.id}`}
          intent={intent}
          onClose={() => setIntent(null)}
          onCreated={(created) => {
            setIntent(null);
            setOpenId(created.id);
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteDialog
          system={deleting}
          onClose={() => setDeleting(null)}
          onArchive={() => {
            archive.mutate({ id: deleting.id, archived: true });
            setDeleting(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * §2.4 — three outcomes the UI must not blur.
 *
 * A system nothing has pinned is deleted outright. A system a leaderboard has been scored by
 * cannot be, and the server refuses with a 409 whose message leads with Archive rather than with
 * the failure. So Archive is offered *before* the attempt as well as after one: for any system
 * that has actually been used, archiving is what was wanted.
 */
function DeleteDialog({
  system,
  onClose,
  onArchive,
}: {
  system: PointsSystem;
  onClose: () => void;
  onArchive: () => void;
}) {
  const { roomId } = useRoom();
  const remove = useDeletePointsSystem(roomId);

  return (
    <Modal
      title={`Delete ${system.name}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="button" onClick={onArchive} className={btnPrimary}>
            Archive instead
          </button>
          <button
            type="button"
            onClick={() => remove.mutate(system.id, { onSuccess: onClose })}
            disabled={remove.isPending}
            className="flex-1 rounded-lg border border-chip-500/40 py-2 text-sm font-medium text-chip-500 hover:bg-chip-500/5 disabled:opacity-40"
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm leading-relaxed text-ink-800/80">
        <p>
          <strong className="text-ink-950">Archiving</strong> hides it from the picker and leaves
          every leaderboard that used it intact and still scoring. It can be restored.
        </p>
        <p>
          <strong className="text-ink-950">Deleting</strong> removes the system and all its
          versions for good, and only works if no leaderboard has ever pinned one. If one has, the
          server will refuse — and archiving is what you wanted.
        </p>
        <ErrorNotice message={describeApiError(remove.error)} />
      </div>
    </Modal>
  );
}
